import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import { formatShortNumber, formatFixed } from '../../utils/number';
import { useAuth } from '../../providers/AuthProvider';
import { SalesPerformanceDataPoint } from './types';

interface DealPerformanceProps {
  data: SalesPerformanceDataPoint[];
  groupBy: 'day' | 'week' | 'month';
}

export const DealPerformance = ({ data, groupBy }: DealPerformanceProps) => {
  const { userInfo } = useAuth();
  const currencySymbol = userInfo?.company_base_currency_symbol || '$';

  const chartData = useMemo(() => {
    const normalized = (Array.isArray(data) ? data : [])
      .map((item) => {
        if (!item) return null;
        const parsedDate = dayjs(item.date);
        if (!parsedDate.isValid()) return null;

        const ordersAmount = Number(
          item.orders_amount ??
          (item as any).total_sales ??
          0
        );
        const invoicesAmount = Number(
          item.invoices_amount ??
          0
        );

        return {
          date: parsedDate.format('YYYY-MM-DD'),
          ordersAmount: Number.isFinite(ordersAmount) ? ordersAmount : 0,
          invoicesAmount: Number.isFinite(invoicesAmount) ? invoicesAmount : 0,
        };
      })
      .filter(
        (
          item: { date: string; ordersAmount: number; invoicesAmount: number } | null
        ): item is { date: string; ordersAmount: number; invoicesAmount: number } => item !== null
      )
      .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));

    return normalized;
  }, [data]);

  const getWeekOfMonth = useCallback((d: dayjs.Dayjs) => {
    const firstDay = d.startOf('month');
    const firstDayMondayBased = (firstDay.day() + 6) % 7;
    const daysFromWeekStart = d.date() - 1 + firstDayMondayBased;
    return Math.floor(daysFromWeekStart / 7) + 1;
  }, []);

  const labelFormatter = useCallback((value: string) => {
    const d = dayjs(value);
    if (!d.isValid()) return value;
    if (groupBy === 'month') return d.format('MMM YYYY');
    if (groupBy === 'week') return `Week ${getWeekOfMonth(d)}`;
    return d.format('MMM DD, YYYY');
  }, [groupBy, getWeekOfMonth]);

  const tickFormatter = useCallback((value: string) => {
    const d = dayjs(value);
    if (!d.isValid()) return value;
    if (groupBy === 'month') return d.format('MMM');
    if (groupBy === 'week') return `W${getWeekOfMonth(d)}`;
    return d.format('MMM DD');
  }, [groupBy, getWeekOfMonth]);

  return (
    <Card className="rounded shadow p-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-xl font-semibold mb-4 flex items-center justify-between">
          <span>Sales Performance</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length ? (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tickFormatter={tickFormatter}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${currencySymbol}${formatShortNumber(Number(v))}`}
              />
              <Tooltip
                labelFormatter={labelFormatter}
                formatter={(val: any, name: string) => {
                  const num = Number(val);
                  if (Number.isNaN(num)) return [val, name];
                  return [`${currencySymbol}${formatFixed(num, 2)}`, name];
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="ordersAmount"
                stroke="#6366f1"
                strokeWidth={2}
                dot={{ strokeWidth: 2 }}
                name={`Orders Amount (${currencySymbol})`}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="invoicesAmount"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ strokeWidth: 2 }}
                name={`Invoices Amount (${currencySymbol})`}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey="invoicesAmount"
                  position="top"
                  content={(props: any) => {
                    const { x, y, value } = props;
                    if (typeof x !== 'number' || typeof y !== 'number') return null;
                    const numericValue = Number(value);
                    if (!Number.isFinite(numericValue)) return null;

                    return (
                      <text
                        x={x}
                        y={y - 8}
                        fill="#0f172a"
                        fontSize={12}
                        textAnchor="middle"
                      >
                        {`${currencySymbol}${formatShortNumber(numericValue)}`}
                      </text>
                    );
                  }}
                />
              </Line>
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="py-16 text-center text-slate-500">No sales performance data available</div>
        )}
      </CardContent>
    </Card>
  );
};
